import React, { useEffect, useState } from "react";

import moment, { Moment } from "moment";
import { RangeValue } from "rc-picker/lib/interface";
import { TFunction, useTranslation } from "react-i18next";

import { DatePicker } from "antd";

import { languageDataPickerConfigMap } from "../../../i18n";

import { Button, ButtonGroupSwitcher } from "./components";
import styles from "./styles.module.scss";

const { RangePicker } = DatePicker;

export interface DateRange {
  from: Moment;
  to: Moment;
}

export interface DateRangeSelectorProps {
  range: DateRange;
  intervals?: Button<DateRange>[];
  onSelect: (range: DateRange) => void;
}

const defaultIntervals = (t: TFunction<"date_range_selector">): Button<DateRange>[] => {
  const today = moment.utc().startOf("day");
  const yesterday = moment.utc(today).subtract(1, "day");
  const weekAgo = moment.utc(today).subtract(1, "week").add(1, "day");
  const monthAgo = moment.utc(today).subtract(1, "month").add(1, "day");
  const yearAgo = moment.utc(today).subtract(1, "year").add(1, "day");

  return [
    { label: t("intervals.today"), value: { from: today, to: today } },
    { label: t("intervals.yesterday"), value: { from: yesterday, to: yesterday } },
    { label: t("intervals.week"), value: { from: weekAgo, to: today } },
    { label: t("intervals.month"), value: { from: monthAgo, to: today } },
    { label: t("intervals.year"), value: { from: yearAgo, to: today } },
  ];
};

interface DateRangeSelectorState {
  range: { from: Moment | null; to: Moment | null };
  activePickerIdx: 1 | 0;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = (props) => {
  const { range, intervals, onSelect } = props;
  const { t, i18n } = useTranslation("date_range_selector");

  const [dates, setDates] = useState<any>(range);
  const [popupIsOpen, setPopupIsOpen] = useState<boolean>(false);

  const isActiveButton = (item: Button<DateRange>) => {
    const { from, to } = item.value;
    return from.isSame(range.from) && to.isSame(range.to);
  };

  const handleOnOpen = (isOpen: boolean) => {
    setPopupIsOpen(isOpen);
  };

  const handleOnChange = (selectedDates: RangeValue<Moment>) => {
    const [from, to] = selectedDates ?? [null, null];
    setDates({ from, to });
  };

  useEffect(() => {
    if (!popupIsOpen) {
      if (!!dates.from && !!dates.to && (!dates.from.isSame(range.from) || !dates.to.isSame(range.to))) {
        onSelect(dates);
      }
    }
  }, [popupIsOpen, dates, range, onSelect]);

  return (
    <div className={styles.wrapper}>
      <ButtonGroupSwitcher
        title={t("interval")}
        buttons={intervals || defaultIntervals(t)}
        onClick={setDates}
        isActive={isActiveButton}
      />
      <RangePicker
        className={styles.datepicker}
        format="YYYY-MM-DD"
        locale={languageDataPickerConfigMap[i18n.language]}
        value={[dates.from, dates.to]}
        allowClear={false}
        allowEmpty={[false, false]}
        onChange={handleOnChange}
        onOpenChange={handleOnOpen}
        inputReadOnly
      />
    </div>
  );
};
